// A map's side panel dragged out as its own module (Ben, 2026-10-31: "in many cases the user will
// want to get a larger, independent view of the pitch area"). The Shot Map's Shot Details and the
// Pass Map's Summary come out by their title onto empty canvas; the map's pitch then fills its
// module. The loose panel stays linked to its map: the map still draws the panel's contents (a
// React portal into the panel module's slot), so clicks, picks and the selected shot work as
// before. Drag it back onto its map to put it back; closing it puts it back too; closing the map
// closes it. One Undo step each; a dashboard keeps it (both modules carry the same link).
//
// A team / match map has a second piece (Ben, 2026-11-01): its players row (Shooters / Passers)
// drags out as the players list, the same way. The map keeps one link per piece (`_panelLink` for
// the details panel, `_playersLink` for the list); a loose module keeps its own as `_panelLink`.

import { useSyncExternalStore } from "react";
import { getModState, setModState } from "../modules/bridge.js";

/** Each map that has a panel, and the title its loose panel module takes. */
export const PANEL_TITLE_OF_MAP: Readonly<Record<string, string>> = {
  "Shot Map": "Shot Details",
  "Pass Map": "Pass Summary",
};
/** Each map whose players row comes out, and the title its loose list takes. */
export const PLAYERS_TITLE_OF_MAP: Readonly<Record<string, string>> = {
  "Shot Map": "Shooters",
  "Pass Map": "Passers",
};
const PANEL_TITLES: ReadonlySet<string> = new Set([...Object.values(PANEL_TITLE_OF_MAP), ...Object.values(PLAYERS_TITLE_OF_MAP)]);

/** Which piece of a map comes out: its details panel, or its players list. */
export type PanelKind = "details" | "players";
const MAP_KEY: Readonly<Record<PanelKind, string>> = { details: "_panelLink", players: "_playersLink" };

function titleOf(mod: Element): string {
  return mod.querySelector(".module-title")?.textContent?.trim() ?? "";
}

/** Is this a loose panel module? */
export function isPanelModule(mod: Element | null): mod is HTMLElement {
  return !!mod && PANEL_TITLES.has(titleOf(mod));
}

/** The link a loose panel carries, or a map's for one of its pieces (details by default), or null. */
export function panelLinkOf(mod: HTMLElement, kind: PanelKind = "details"): string | null {
  return getModState<string>(mod, MAP_KEY[kind]) ?? null;
}

/** The other module holding `link` under any piece's key. */
function holderOf(link: string, not: HTMLElement): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(".placed-module")) {
    if (el !== not && (panelLinkOf(el, "details") === link || panelLinkOf(el, "players") === link)) return el;
  }
  return null;
}

/** A loose panel's map, or a map's loose piece of this kind. */
export function panelPartner(mod: HTMLElement, kind: PanelKind = "details"): HTMLElement | null {
  const link = isPanelModule(mod) ? panelLinkOf(mod) : panelLinkOf(mod, kind);
  return link ? holderOf(link, mod) : null;
}

// --- the slots a loose panel offers its map ------------------------------------------------------

const slots = new Map<string, HTMLElement>();
const listeners = new Set<() => void>();
function changed(): void {
  for (const l of listeners) l();
}

/** A loose panel module's slot appears / goes (MapPanelModule). */
export function setPanelSlot(link: string, el: HTMLElement | null): void {
  if (el) slots.set(link, el);
  else if (slots.has(link)) slots.delete(link);
  changed();
}

/** The slot of this link's loose panel, re-rendering when it comes or goes. */
export function usePanelSlot(link: string | null): HTMLElement | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => (link ? (slots.get(link) ?? null) : null),
  );
}

// --- out, back, closing --------------------------------------------------------------------------

let pending: { map: HTMLElement; link: string; kind: PanelKind } | null = null;

/** A map's panel (its title) or players row starts coming out (pressed and moved). */
export function beginPanelDetach(map: HTMLElement, kind: PanelKind = "details"): { title: string; link: string } {
  const title = (kind === "details" ? PANEL_TITLE_OF_MAP : PLAYERS_TITLE_OF_MAP)[titleOf(map)];
  if (!title) throw new Error(`beginPanelDetach: ${titleOf(map)} has no ${kind} panel`);
  const link = `panel-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  pending = { map, link, kind };
  return { title, link };
}

/** The panel module was placed (menu-drag's drop): its map lets go of the panel. Holds no Undo
 *  step: the drag's does. Returns the map to refresh, or null for any other module. */
export function completePanelDetach(placed: HTMLElement): HTMLElement | null {
  if (!pending || panelLinkOf(placed) !== pending.link) return null;
  const { map, link, kind } = pending;
  pending = null;
  setModState(map, MAP_KEY[kind], link);
  return map;
}

/** A loose panel dragged onto its own map: put back. */
export function panelGoesBack(source: HTMLElement, target: HTMLElement): boolean {
  return isPanelModule(source) && panelPartner(source) === target;
}

/** Put a loose panel back into its map (it shows inline again); returns the map to refresh. */
export function reattachPanel(panel: HTMLElement): HTMLElement | null {
  const map = panelPartner(panel);
  const link = panelLinkOf(panel);
  if (map) for (const kind of ["details", "players"] as const) if (panelLinkOf(map, kind) === link) setModState(map, MAP_KEY[kind], undefined);
  return map;
}
